import React, { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import api from '../../apiService';
import type { OrganizationSummary } from '../../apiService';
import { formatDateTime } from '../../utils/formatDateTime';
import { isMcpOnlyModuleMap } from '../../utils/moduleAccess';

interface InviteUserFormProps {
    authToken: string;
    onInvited: () => void;
}

interface InvitedUser {
    email: string;
    name: string;
    // Only set when email_sent === false (backup channel for delivery failures).
    invite_link: string | null;
    invite_expires_at: string;
    email_sent: boolean;
}

function InviteUserForm({ authToken, onInvited }: InviteUserFormProps) {
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [company, setCompany] = useState('');
    const [organizations, setOrganizations] = useState<OrganizationSummary[]>([]);
    const [organizationsLoading, setOrganizationsLoading] = useState(true);
    const [organizationError, setOrganizationError] = useState<string | null>(null);
    const [role, setRole] = useState<'user' | 'admin'>('user');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<InvitedUser | null>(null);
    const [copyConfirm, setCopyConfirm] = useState(false);

    useEffect(() => {
        let active = true;
        setOrganizationsLoading(true);
        setOrganizationError(null);
        setOrganizations([]);
        setCompany('');
        api.listOrganizations(authToken, { includeFree: false })
            .then((result) => {
                if (!active) return;
                const rows = Array.isArray(result?.organizations) ? [...result.organizations] : [];
                rows.sort((a, b) =>
                    (a.display_name || a.company_claim).localeCompare(b.display_name || b.company_claim),
                );
                setOrganizations(rows);
                if (rows.length === 1) setCompany(rows[0].company_claim);
            })
            .catch((e: any) => {
                if (active) setOrganizationError(e?.message || 'Failed to load organizations');
            })
            .finally(() => {
                if (active) setOrganizationsLoading(false);
            });
        return () => {
            active = false;
        };
    }, [authToken]);

    const selectedOrganization = organizations.find((org) => org.company_claim === company);
    // An org admin sees only their own organization, and a company signed in
    // before its organization doc existed has none. The invite still goes to the
    // admin's own company (the backend sets it), so there is nothing to pick.
    const noOrganizationChoice =
        !organizationsLoading && !organizationError && organizations.length === 0;

    const reset = () => {
        setEmail('');
        setName('');
        setCompany(organizations.length === 1 ? organizations[0].company_claim : '');
        setRole('user');
        setError(null);
        setSuccess(null);
        setCopyConfirm(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if ((!company && !noOrganizationChoice) || organizationsLoading || organizationError) return;
        setError(null);
        setSuccess(null);
        setIsLoading(true);
        try {
            const result = await api.inviteUser(authToken, {
                email: email.trim(),
                name: name.trim(),
                company: company || undefined,
                role,
            });
            setSuccess(result);
            onInvited();
        } catch (e: any) {
            setError(e?.message || 'Invite failed');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopy = async () => {
        if (!success?.invite_link) return;
        try {
            await navigator.clipboard.writeText(success.invite_link);
            setCopyConfirm(true);
            setTimeout(() => setCopyConfirm(false), 2000);
        } catch {
            // Clipboard API blocked (insecure context, permissions). Fall back to selection.
            const el = document.getElementById('invite-link-display') as HTMLInputElement | null;
            el?.select();
        }
    };

    if (success) {
        const expiresOn = formatDateTime(success.invite_expires_at, { context: 'date' });
        return (
            <div className="max-w-lg bg-surface rounded-md border border-border-default p-6">
                <h2 className="flex items-center gap-2 text-lg font-semibold text-success">
                    <CheckCircle2 size={20} strokeWidth={1.5} aria-hidden="true" />
                    <span>Invited {success.name} ({success.email})</span>
                </h2>
                {success.email_sent ? (
                    <p className="text-sm text-fg-secondary mt-2">
                        An email was sent with a link to set their password. The link
                        expires on {expiresOn}. If they don't receive it, use{' '}
                        <strong>Resend invite</strong> in the Users tab.
                    </p>
                ) : (
                    <>
                        <p className="flex items-start gap-2 text-sm text-danger mt-2">
                            <AlertTriangle size={16} strokeWidth={1.5} className="flex-shrink-0" aria-hidden="true" />
                            <span>
                                Email delivery failed. Share the link below manually.
                                It expires on {expiresOn}.
                            </span>
                        </p>
                        <div className="mt-4">
                            <label className="text-xs font-medium text-fg-secondary uppercase tracking-wider">
                                Invite link (one-time)
                            </label>
                            <input
                                id="invite-link-display"
                                readOnly
                                value={success.invite_link || ''}
                                className="mt-1 w-full rounded-md border border-border-default px-3 py-2 text-sm bg-bg-secondary text-fg-secondary font-mono"
                                onFocus={(e) => e.currentTarget.select()}
                            />
                        </div>
                    </>
                )}

                <div className="mt-4 flex gap-2">
                    {!success.email_sent && success.invite_link && (
                        <button
                            onClick={handleCopy}
                            className="px-4 py-2 text-sm rounded-md border border-border-default text-fg hover:bg-bg-secondary"
                        >
                            {copyConfirm ? '✓ Copied' : 'Copy invite link'}
                        </button>
                    )}
                    <button
                        onClick={reset}
                        className="px-4 py-2 text-sm rounded-md bg-accent text-white hover:bg-accent-hover"
                    >
                        Invite another
                    </button>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="max-w-lg space-y-4">
            <div>
                <label className="block text-sm font-medium text-fg mb-1">Email</label>
                <input
                    type="email"
                    required
                    autoComplete="off"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-md border border-border-default px-3 py-2 text-sm focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                    placeholder="user@example.com"
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-fg mb-1">Name</label>
                <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full rounded-md border border-border-default px-3 py-2 text-sm focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                    placeholder="First and last name"
                />
            </div>
            <div>
                <label htmlFor="invite-organization" className="block text-sm font-medium text-fg mb-1">Organization</label>
                {noOrganizationChoice ? (
                    <p id="invite-organization" className="text-sm text-fg-secondary">
                        This person joins your organization.
                    </p>
                ) : (
                    <select
                        id="invite-organization"
                        required
                        value={company}
                        disabled={organizationsLoading || !!organizationError || organizations.length === 1}
                        onChange={(e) => setCompany(e.target.value)}
                        className="w-full rounded-md border border-border-default px-3 py-2 text-sm focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent bg-surface"
                    >
                        <option value="" disabled>Select an organization</option>
                        {organizations.map((org) => (
                            <option key={org.company_claim} value={org.company_claim}>
                                {org.display_name || org.company_claim}
                            </option>
                        ))}
                    </select>
                )}
                {organizationsLoading && (
                    <p className="mt-1 text-sm text-fg-secondary">Loading organizations…</p>
                )}
                {selectedOrganization && isMcpOnlyModuleMap(selectedOrganization.effective_modules) && (
                    <p className="mt-1 text-sm text-fg-secondary">
                        MCP only: the invite email explains how to connect an AI assistant, and the person lands on Connect assistant after signing in.
                    </p>
                )}
            </div>
            <div>
                <label className="block text-sm font-medium text-fg mb-1">Role</label>
                <select
                    value={role}
                    onChange={(e) => setRole(e.target.value as 'user' | 'admin')}
                    className="w-full rounded-md border border-border-default px-3 py-2 text-sm bg-surface"
                >
                    <option value="user">user</option>
                    <option value="admin">admin</option>
                </select>
            </div>

            {error && <p className="text-sm text-danger">{error}</p>}
            {organizationError && <p className="text-sm text-danger">{organizationError}</p>}

            <button
                type="submit"
                disabled={
                    isLoading ||
                    organizationsLoading ||
                    !!organizationError ||
                    (!company && !noOrganizationChoice)
                }
                className="px-4 py-2 text-sm rounded-md bg-accent text-white hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {isLoading ? 'Sending invite…' : 'Send invite'}
            </button>
        </form>
    );
}

export default InviteUserForm;
